import { defineMessages } from "react-intl";
import classNames from "classnames";

import BudgetAmount from "./BudgetAmount";
import { useFormatMessage } from "../../../Intl/Context";
import { useMemo } from "react";

const BudgetBalance = props => {
  const { total, goal, unit = "€", minAmount, maxAmount } = props;
  const colorClass = total < 0 ? "bg-danger" : "bg-success";
  const hasNegativeValues = minAmount < 0;

  const t = useFormatMessage();
  const label = t(intlMessages.budgetBalance);

  const className = classNames("budget-balance", props.className, {
    "budget-balance-negative": total < 0,
    "budget-balance-positive": total >= 0,
    "negative-values-hidden": !hasNegativeValues
  });

  const progressClassName = classNames("progress-bar", colorClass);

  // Calculate the width of the progress bar based on the total amount and the min/max
  // amounts configured for the budget.
  const progressWidth = useMemo(() => {
    // If negative values are allowed, the maximum width is 50% on each side of the
    // progress bar. Otherwise, it's 100%.
    const maxValue = hasNegativeValues ? 50 : 100;

    if (total >= maxAmount || (hasNegativeValues && total <= minAmount)) {
      return maxValue;
    } else if (hasNegativeValues && total < 0) {
      return (total / minAmount) * maxValue;
    } else if (total > 0) {
      return (total / maxAmount) * maxValue;
    }

    return 0;
  }, [hasNegativeValues, total, maxAmount, minAmount]);

  // Depending on whether the total is negative or positive, we move the progress bar
  // to the left or to the right of the center point (50%).
  const marginWidth = useMemo(() => {
    if (!hasNegativeValues) {
      return 0;
    } else if (total >= 0) {
      return 50;
    } else {
      return 50 - progressWidth;
    }
  }, [hasNegativeValues, total, progressWidth]);

  // Goal message logic
  const goalMessage = useMemo(() => {
    if (!goal) return null;

    if (total >= goal) {
      return t(intlMessages.goalReached);
    } else {
      const remaining = goal - total;
      return t(intlMessages.goalRemaining, { amount: remaining, unit });
    }
  }, [goal, total, unit, t]);

  return (
    <div className={className}>
      <div>
        <span className="margin-right-xxs">{label}</span>

        <BudgetAmount amount={total} unit={unit} />
      </div>

      {goalMessage && (
        <div className="margin-top-xxs text-small">
          <strong>{goalMessage}</strong>
        </div>
      )}

      <div className="margin-top-xs">
        <div className="progress margin-bottom-none">
          <div
            className={progressClassName}
            role="progressbar"
            style={{ marginLeft: `${marginWidth}%`, width: `${progressWidth}%` }}
            aria-valuenow={total}
            aria-valuemin={minAmount}
            aria-valuemax={maxAmount}
          >
            <span className="sr-only">{total}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetBalance;

const intlMessages = defineMessages({
  budgetBalance: {
    id: "form.field.budget.budgetBalance",
    defaultMessage: "Équilibre du budget :"
  },
  goalReached: {
    id: "form.field.budget.goalReached",
    defaultMessage: "Vous avez atteint l'objectif !"
  },
  goalRemaining: {
    id: "form.field.budget.goalRemaining",
    defaultMessage: "{amount, number} {unit} restants"
  }
});
